import React from 'react';

/**
 * Fallback "êm" cho <Suspense> khi chuyển route lazy.
 *
 * - **Trễ {delay}ms** trước khi hiện UI: nếu chunk JS tải xong trước ngưỡng này
 *   thì user KHÔNG thấy bất kỳ loading state nào — chuyển trang cảm giác instant.
 * - **Khi vẫn đang tải** sau ngưỡng: chỉ hiện 1 thanh progress mảnh (h-1) ở đầu
 *   vùng nội dung, thay vì overlay xám toàn màn hình. Header/Footer/Breadcrumbs
 *   không bị che → giữ ngữ cảnh.
 * - Có `aria-live` để screen-reader vẫn nhận biết.
 *
 * Hợp với hầu hết route lazy nhỏ (50–200ms): user gần như không bao giờ thấy fallback.
 */
const RouteTransitionFallback: React.FC<{ delay?: number; label?: string }> = ({
    delay = 200,
    label = 'Đang tải nội dung trang...',
}) => {
    const [show, setShow] = React.useState(false);

    React.useEffect(() => {
        const id = window.setTimeout(() => setShow(true), delay);
        return () => window.clearTimeout(id);
    }, [delay]);

    return (
        <div
            className={`pointer-events-none transition-opacity duration-200 ${show ? 'opacity-100' : 'opacity-0'}`}
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <span className="sr-only">{label}</span>
            <div className="relative mx-auto mt-2 h-1 w-full max-w-3xl overflow-hidden rounded-full bg-teal-100/60">
                <div className="absolute inset-y-0 left-0 w-1/3 animate-route-progress rounded-full bg-gradient-to-r from-teal-400 via-sky-500 to-teal-400" />
            </div>
        </div>
    );
};

export default RouteTransitionFallback;
